import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { TemplateRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, Input, input } from '@angular/core';

import { TranslatePipe } from '@core/i18n-legacy';
import { AssignedLocationsInfoContainerComponent } from '@core/locations-smart-ui';
import type { TOfferKind } from '@core/offers-types';
import type { OfferSelectOption } from '@core/offers-ui';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import type { SelectOption } from '@core/user-interface-legacy';
import {
  RtInput,
  TagComponent,
  InputComponent,
  IconComponent,
  TruncateComponent,
  ButtonDropdownIndicatorDirective,
  ButtonComponent,
  SelectComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-offer-select',
  templateUrl: './offer-select.component.html',
  styleUrls: ['./offer-select.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectComponent,
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    OfferStatusIndicatorComponent,
    TruncateComponent,
    IconComponent,
    InputComponent,
    AssignedLocationsInfoContainerComponent,
    TagComponent,
    TranslatePipe,
  ],
})
export class OfferSelectComponent extends RtInput<number> {
  public readonly offers = input<OfferSelectOption[]>([]);
  public readonly additionalOptions = input<SelectOption[]>([]);
  public readonly additionalOptionsPlacement = input<'top' | 'bottom'>('top');
  public readonly showStatus = input<boolean>(true);
  public readonly inputVariant = input<'rounded' | 'plain'>('rounded');
  public readonly buttonSize = input<'small' | 'medium'>('medium');
  public readonly showStageName = input<boolean>(false);

  // SelectComponent props
  public readonly hasInputTrigger = input<boolean>(false);
  public readonly placeholder = input<string>();
  public readonly searchPlaceholder = input<string>();
  public readonly tabIndex = input<number>();
  public readonly hasSearch = input<boolean>();
  public readonly searchCaseSensitive = input<boolean>();
  public readonly limit = input<number>();
  public readonly noResultsMessage = input<string>();
  public readonly includeEmptyOption = input<boolean>();
  public readonly emptyOptionLabel = input<string>();
  public readonly multiple = input<boolean>();
  public readonly closeOnSelect = input<boolean>();
  public readonly minDropdownWidth = input<number>();
  public readonly maxDropdownWidth = input<number>();
  public readonly dropdownPositions = input<ConnectionPositionPair[]>();
  public readonly dropdownIconHasMargin = input<boolean>();
  public readonly includeOfferLocation = input<boolean>(false);
  public readonly headerTemplate = input<TemplateRef<any>>();
  public readonly showCheckmark = input(false);

  public readonly options = computed(() => {
    const offerOptions = this.offers().map(offer => ({
      _isOffer: true,
      label: offer.title,
      value: offer.id,
      count: offer.count,
      disabled: offer.disabled,
      icon: offer.icon,
      ...(this.includeOfferLocation()
        ? { location: offer.location, locationIds: offer.locationIds }
        : {}),
    }));

    return this.additionalOptionsPlacement() === 'bottom'
      ? [...offerOptions, ...this.additionalOptions()]
      : [...this.additionalOptions(), ...offerOptions];
  });

  @Input() public buttonVariant: 'plain' | 'tertiary' | 'outline' = 'plain';

  public getOfferStatus(value: number): { kind: TOfferKind; status: string } | undefined {
    return <{ kind: TOfferKind; status: string }>this.offers().find(o => o.id === value);
  }

  public getStageName(value: number) {
    return this.offers().find(o => o.id === value)?.stageName;
  }
}
